import { styled } from "@pigment-css/react";
import { pressInteraction } from "@acto/taskpane/field-sales-2/styles/pressInteraction";
import { CALENDAR_DAY_HEIGHT } from "./constants";
import type { CalendarDayState } from "./types";

const DayButton = styled("button")<{ dayState: CalendarDayState; today?: boolean }>(({ theme }) => ({
  width: "100%",
  minWidth: 0,
  height: CALENDAR_DAY_HEIGHT,
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxSizing: "border-box",
  borderRadius: theme.fieldSalesTwo.radius.circular,
  border: `${theme.strokeWidth.thin} solid transparent`,
  background: "transparent",
  cursor: "pointer",
  userSelect: "none",
  WebkitUserSelect: "none",
  WebkitTapHighlightColor: "transparent",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[520],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.subtle,
  color: theme.fieldSalesTwo.colors.neutral[900],
  transition: `background-color ${theme.fieldSalesTwo.motion.duration.fast} ${theme.fieldSalesTwo.motion.easing.standard}, border-color ${theme.fieldSalesTwo.motion.duration.fast} ${theme.fieldSalesTwo.motion.easing.standard}, transform ${theme.fieldSalesTwo.motion.duration.fast} ${theme.fieldSalesTwo.motion.easing.standard}`,
  variants: [
    {
      props: { dayState: "default" },
      style: { borderColor: theme.fieldSalesTwo.colors.neutral[200] },
    },
    {
      props: { dayState: "inRange" },
      style: { background: theme.fieldSalesTwo.colors.neutral[200] },
    },
    {
      props: { dayState: "selected" },
      style: { background: theme.fieldSalesTwo.colors.neutral[900], color: "#fff" },
    },
    {
      props: { dayState: "disabled" },
      style: { color: theme.fieldSalesTwo.colors.neutral[500], cursor: "default" },
    },
    {
      // "Today" indicator: a darker neutral ring. Applied after the dayState variants so it
      // overrides the default neutral border; under a selected cell it reads as a grey outline.
      props: { today: true },
      style: { borderColor: theme.fieldSalesTwo.colors.neutral[400] },
    },
  ],
  ...pressInteraction(theme),
}));

const MeetingDot = styled("span")(({ theme }) => ({
  position: "absolute",
  bottom: theme.fieldSalesTwo.spacing[8],
  left: "50%",
  transform: "translateX(-50%)",
  width: "3px",
  height: "3px",
  borderRadius: theme.fieldSalesTwo.radius.circular,
  background: "currentColor",
}));

interface CalendarDayProps {
  label: string;
  ariaLabel: string;
  state?: CalendarDayState;
  hasMeeting?: boolean;
  isToday?: boolean;
  onSelect?: () => void;
}

export function CalendarDay({
  label,
  ariaLabel,
  state = "default",
  hasMeeting = false,
  isToday = false,
  onSelect,
}: CalendarDayProps) {
  const disabled = state === "disabled";

  return (
    <DayButton
      type="button"
      dayState={state}
      today={isToday}
      aria-label={ariaLabel}
      aria-pressed={state === "selected"}
      disabled={disabled}
      onClick={disabled ? undefined : onSelect}
    >
      {label}
      {hasMeeting && <MeetingDot aria-hidden="true" />}
    </DayButton>
  );
}
